/**
 * Session Storage Sync Utility
 * Syncs sessionStorage across tabs using BroadcastChannel with localStorage fallback
 * 
 * SECURITY REQUIREMENT:
 * - ALL data stored ONLY in sessionStorage (cleared on browser/tab close)
 * - localStorage used ONLY as a temporary message bus (data deleted immediately)
 * - No persistent storage of any auth data
 */

import { getSessionPayload, storeSessionData } from './session-storage-helpers';
import { AUTH_SYNC_SIGNAL_KEY } from '@config/shared/storage-keys';

const CHANNEL_NAME = 'session_sync';
const STORAGE_SYNC_KEY = AUTH_SYNC_SIGNAL_KEY;
const SYNC_TIMEOUT = 2000; // 2 seconds to wait for response

let channel = null;
let storageListener = null;
let syncCallbacks = [];

/**
 * Initialize session sync for a new tab
 * Requests session data from other tabs if current tab has no session
 */
export function initSessionSync() {
  if (globalThis.window === undefined) {
    return;
  }

  if (typeof BroadcastChannel === 'undefined') {
    // Fallback to localStorage events (older browsers)
    initLocalStorageSync();
  } else {
    initBroadcastChannelSync();
  }
}

/**
 * Register a callback to be called when session data changes from another tab
 */
export function onSessionChange(callback) {
  syncCallbacks.push(callback);
  
  // Return unsubscribe function
  return () => {
    syncCallbacks = syncCallbacks.filter(cb => cb !== callback);
  };
}

/**
 * Modern sync using BroadcastChannel
 */
function initBroadcastChannelSync() {
  // Create channel if not exists
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
  }

  // Check if we already have session data
  const hasSession = sessionStorage.getItem('accessToken');

  // Set up message handler for ALL tabs.
  // NOTE: handleSyncMessage re-reads the session live from sessionStorage on
  // every message — it must NOT close over `hasSession`, which is only a
  // snapshot taken at init time and goes stale the moment a session syncs in.
  channel.onmessage = (event) => {
    handleSyncMessage(event.data);
  };

  // If new tab, request session
  if (!hasSession) {
    channel.postMessage({ type: 'REQUEST_SESSION' });
    
    setTimeout(() => {
      if (!sessionStorage.getItem('accessToken')) {
        console.debug('[SessionSync] No active session found in other tabs');
      }
    }, SYNC_TIMEOUT);
  }
}

/**
 * Central handler for incoming sync messages (BroadcastChannel & localStorage)
 */
function handleSyncMessage(data) {
  if (data.type === 'SESSION_DATA') {
    // Re-read the session live (never a captured flag): a stale snapshot is
    // what caused the multi-tab reload loop.
    const before = sessionStorage.getItem('accessToken');
    storeSessionData(data);
    const after = sessionStorage.getItem('accessToken');

    if (before) {
      // Already logged in → just notify subscribers, never reload.
      syncCallbacks.forEach(callback => callback(data));
    } else if (after && after !== before) {
      // Genuine logged-out → logged-in transition: reload once to boot the app
      // in an authenticated state.
      globalThis.location.reload();
    }
    // else: nothing meaningful arrived (empty/identical payload) → do NOT
    // reload. This is what breaks the request → answer → reload loop.
    return;
  }

  if (data.type === 'REQUEST_SESSION' && sessionStorage.getItem('accessToken')) {
    sendSessionData();
    return;
  }

  if (data.type === 'LOGOUT') {
    console.debug('[SessionSync] Received logout signal, clearing session...');
    sessionStorage.clear();
    const signedOut = data.signedOut === true;
    syncCallbacks.forEach(callback => callback({ type: 'LOGOUT', signedOut }));
  }
}

/**
 * Send session data via the active channel (BroadcastChannel or localStorage)
 */
function sendSessionData() {
  const sessionData = getSessionPayload('SESSION_DATA');

  if (channel) {
    channel.postMessage(sessionData);
  } else {
    try {
      localStorage.setItem(STORAGE_SYNC_KEY, JSON.stringify(sessionData));
      setTimeout(() => localStorage.removeItem(STORAGE_SYNC_KEY), 1000);
    } catch (error) {
      console.error('[SessionSync] Failed to send session data:', error);
    }
  }
}

/**
 * Fallback sync using localStorage events
 * NOTE: localStorage events only fire in OTHER tabs, not the originating tab
 * This is a limitation but better than no sync at all
 */
function initLocalStorageSync() {
  console.debug('[SessionSync] Using localStorage fallback (BroadcastChannel not supported)');

  const hasSession = sessionStorage.getItem('accessToken');

  storageListener = (event) => {
    if (event.key !== STORAGE_SYNC_KEY || !event.newValue) return;
    try {
      const data = JSON.parse(event.newValue);
      // Live re-read inside handleSyncMessage — do not pass a stale snapshot.
      handleSyncMessage(data);
      // Clean up sync signal from localStorage
      localStorage.removeItem(STORAGE_SYNC_KEY);
    } catch (error) {
      console.error('[SessionSync] Failed to parse sync data:', error);
    }
  };

  globalThis.addEventListener('storage', storageListener);

  // New tab — request session from other tabs
  if (!hasSession) {
    try {
      localStorage.setItem(STORAGE_SYNC_KEY, JSON.stringify({
        type: 'REQUEST_SESSION',
        timestamp: Date.now()
      }));
      setTimeout(() => localStorage.removeItem(STORAGE_SYNC_KEY), SYNC_TIMEOUT);
    } catch (error) {
      console.error('[SessionSync] Failed to request session:', error);
    }
  }
}

/**
 * Cleanup on unmount
 */
export function cleanupSessionSync() {
  if (channel) {
    channel.close();
    channel = null;
  }
  
  if (storageListener) {
    globalThis.removeEventListener('storage', storageListener);
    storageListener = null;
  }
  
  // Clean up any leftover sync signals
  try {
    localStorage.removeItem(STORAGE_SYNC_KEY);
  } catch (error) {
    console.debug('[SessionSync] localStorage cleanup failed:', error);
  }
}

/**
 * Notify other tabs when session data changes.
 * @param {string[]|null} includeKeys - When set, only broadcast these keys (e.g.
 *   a features/paths change broadcasts token-free to avoid clobbering siblings'
 *   rotated tokens). Omit for a full session broadcast (login, token refresh).
 */
export function notifySessionChange(includeKeys = null) {
  console.debug('[SessionSync] Broadcasting session change to other tabs');
  broadcastMessage(getSessionPayload('SESSION_DATA', includeKeys));
}

/**
 * Send a message via BroadcastChannel or localStorage fallback
 */
function broadcastMessage(data) {
  if (typeof BroadcastChannel === 'undefined') {
    try {
      localStorage.setItem(STORAGE_SYNC_KEY, JSON.stringify(data));
      setTimeout(() => localStorage.removeItem(STORAGE_SYNC_KEY), 1000);
    } catch (error) {
      console.error('[SessionSync] Failed to broadcast message:', error);
    }
  } else {
    if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(data);
  }
}

/**
 * @param {Object} [options]
 * @param {boolean} [options.signedOut=false] - true for an explicit sign-out
 *   by the user (vs. a session that expired or lost access), so the other
 *   tabs don't silently sign back in on an ActiveDirectory tenant
 */
export function notifyLogout({ signedOut = false } = {}) {
  console.debug('[SessionSync] Broadcasting logout to other tabs');
  broadcastMessage({ type: 'LOGOUT', signedOut, timestamp: Date.now() });
}
